<template>
  <div>
    <el-dialog
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      width="1200px"
      class="ai-report-dialog"
      @close="handleClose"
    >
      <template #title>
        <div>
          <span class="dialog-title">{{ typeConfig.title }}</span>
          <span class="dialog-sub" v-if="type == 'shop' || type == 'sence'"
            >统计时间：{{ cardInfo.subTitle }}</span
          >
        </div>
      </template>
      <div class="ai-report-content">
        <!-- 顶部操作栏 -->
        <div class="ai-report-header">
          <template v-if="type === 'shop'">
            <!-- Tabs区域 - 只有shop类型显示 -->
            <el-tabs v-model="activeTab" class="ai-report-tabs">
              <el-tab-pane label="销售分析" name="SALES" />
              <el-tab-pane label="客户分析" name="CUSTOMER" />
              <el-tab-pane label="会员分析" name="MEMBER" />
              <el-tab-pane label="商品分析" name="PRODUCT" />
            </el-tabs>
          </template>
          <template v-else>
            <div class="ai-report-selector">
              <span class="selector-label"
                >{{ typeConfig.selectorLabel }}：</span
              >
              <el-select
                v-model="selectedValue"
                placeholder="请选择"
                size="small"
                style="width: 300px"
                @change="selectChange"
              >
                <el-option
                  v-for="item in selectorOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </div>
          </template>
          <div
            class="ai-report-download-message"
            v-if="type === 'message'"
          >
            <div class="ai-report-download" v-if="cardInfo.showSummary">
              <img
                class="ai-report-download__img"
                :src="cdnUrl + 'ai-report-download-ppt.svg'"
                alt=""
              />
              <el-button type="text" size="mini" @click="handleDownload('ppt')">
                下载汇总PPT
              </el-button>
            </div>
            <div class="ai-report-download">
              <img
                class="ai-report-download__img"
                :src="cdnUrl + 'ai-report-download-xlsx.svg'"
                alt=""
              />
              <el-button type="text" size="mini" @click="handleDownload">
                下载Excel
              </el-button>
            </div>
          </div>
          <div
            v-else
            :class="[
              'ai-report-download',
              { 'ai-report-download__shop': type === 'shop' },
            ]"
          >
            <img
              class="ai-report-download__img"
              :src="cdnUrl + 'ai-report-download.svg'"
              alt=""
            />
            <el-button type="text" size="mini" @click="handleDownload">
              下载
            </el-button>
          </div>
        </div>

        <!-- 主要内容区域 -->
        <div class="ai-report-main">
          <!-- 销售分析tab：四个表格 -->
          <template v-if="type === 'shop' && activeTab === 'SALES'">
            <div
              v-for="(report, index) in saleAnalysisList"
              :key="'sale-' + index"
              class="ai-report-sale-section"
            >
              <div class="ai-report-sale-title">{{ report.reportName }}</div>
              <div v-if="report.noDataText" class="ai-report-sale-desc">
                {{ report.noDataText }}
              </div>
              <div v-else class="ai-report-table-wrapper">
                <div class="ai-report-table-scroll">
                  <el-table
                    :data="report.tableData"
                    border
                    style="width: 100%"
                    size="small"
                    :max-height="maxHeight"
                  >
                    <template slot="empty">
                      <empty />
                    </template>
                    <el-table-column
                      v-for="column in saleAnalysisColumns"
                      :key="column.prop"
                      :prop="column.prop"
                      :label="column.label"
                      :width="column.width"
                      :align="column.align ? column.align : 'right'"
                      :formatter="
                        column.formatter ? column.formatter : undefined
                      "
                    />
                  </el-table>
                </div>
              </div>
              <div
                v-for="(analysis, aIndex) in report.analyses"
                :key="'analysis-' + index + '-' + aIndex"
                class="ai-report-paragraph-section"
              >
                <div class="ai-report-subtitle">{{ analysis.title }}</div>
                <div
                  class="ai-report-text global-ai-content"
                >
                  <div
                    v-for="(block, idx) in renderedBlocks(analysis.content)"
                    :key="idx"
                  >
                    <!-- 普通 markdown 文本 -->
                    <div
                      v-if="block.type === 'markdown'"
                      class="md-content"
                      v-html="block.html"
                    ></div>
                    <!-- 表格块：按钮 + 表格 -->
                    <div
                      v-else-if="block.type === 'table'"
                      class="table-block"
                    >
                      <md-table :html="block.html">
                        <div v-html="block.html"></div>
                      </md-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 客户分析tab：四个表格 -->
          <template v-else-if="type === 'shop' && activeTab === 'CUSTOMER'">
            <div
              v-for="(report, index) in customerAnalysisList"
              :key="'customer-' + index"
              class="ai-report-sale-section"
            >
              <div class="ai-report-sale-title">{{ report.reportName }}</div>
              <div v-if="report.noDataText" class="ai-report-sale-desc">
                {{ report.noDataText }}
              </div>
              <div v-else class="ai-report-table-wrapper">
                <div class="ai-report-table-scroll">
                  <el-table
                    :data="report.tableData"
                    border
                    style="width: 100%"
                    size="small"
                    :span-method="
                      ({ row, column, rowIndex, columnIndex }) =>
                        customerSpanMethod({
                          row,
                          column,
                          rowIndex,
                          columnIndex,
                          tableData: report.tableData,
                          reportType: report.reportType,
                        })
                    "
                    :max-height="maxHeight"
                  >
                    <template slot="empty">
                      <empty />
                    </template>
                    <el-table-column
                      v-for="column in customerAnalysisCommonColumns[
                        report.reportType
                      ]"
                      :key="column.prop"
                      :prop="column.prop"
                      :label="column.label"
                      :width="column.width"
                      :align="column.align ? column.align : 'right'"
                    />
                  </el-table>
                </div>
              </div>
              <div
                v-for="(analysis, aIndex) in report.analyses"
                :key="'analysis-' + index + '-' + aIndex"
                class="ai-report-paragraph-section"
              >
                <div class="ai-report-subtitle">{{ analysis.title }}</div>
                <div
                  class="ai-report-text global-ai-content"
                >
                  <div
                    v-for="(block, idx) in renderedBlocks(analysis.content)"
                    :key="idx"
                  >
                    <!-- 普通 markdown 文本 -->
                    <div
                      v-if="block.type === 'markdown'"
                      class="md-content"
                      v-html="block.html"
                    ></div>
                    <!-- 表格块：按钮 + 表格 -->
                    <div
                      v-else-if="block.type === 'table'"
                      class="table-block"
                    >
                      <md-table :html="block.html">
                        <div v-html="block.html"></div>
                      </md-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 会员分析tab -->
          <template v-else-if="type === 'shop' && activeTab === 'MEMBER'">
            <div
              v-for="(report, index) in memberAnalysisList"
              :key="'member-' + index"
              class="ai-report-sale-section"
            >
              <div class="ai-report-sale-title">{{ report.reportName }}</div>
              <div v-if="report.noDataText" class="ai-report-sale-desc">
                {{ report.noDataText }}
              </div>
              <div v-else class="ai-report-table-wrapper">
                <div class="ai-report-table-scroll">
                  <el-table
                    :data="report.tableData"
                    border
                    style="width: 100%"
                    size="small"
                    :span-method="
                      report.reportType === 'POINTS_CHANGE_ANALYSIS'
                        ? ({ row, column, rowIndex, columnIndex }) =>
                            memberSpanMethod({
                              row,
                              column,
                              rowIndex,
                              columnIndex,
                              tableData: report.tableData,
                            })
                        : undefined
                    "
                    :max-height="maxHeight"
                  >
                    <template slot="empty">
                      <empty />
                    </template>
                    <el-table-column
                      v-for="column in memberAnalysisColumns[report.reportType]"
                      :key="column.prop"
                      :prop="column.prop"
                      :label="column.label"
                      :min-width="column.minWidth"
                      :align="column.align || 'center'"
                    />
                  </el-table>
                </div>
              </div>
              <div
                v-for="(analysis, aIndex) in report.analyses"
                :key="'analysis-' + index + '-' + aIndex"
                class="ai-report-paragraph-section"
              >
                <div class="ai-report-subtitle">{{ analysis.title }}</div>
                <div
                  class="ai-report-text global-ai-content"
                >
                  <div
                    v-for="(block, idx) in renderedBlocks(analysis.content)"
                    :key="idx"
                  >
                    <!-- 普通 markdown 文本 -->
                    <div
                      v-if="block.type === 'markdown'"
                      class="md-content"
                      v-html="block.html"
                    ></div>
                    <!-- 表格块：按钮 + 表格 -->
                    <div
                      v-else-if="block.type === 'table'"
                      class="table-block"
                    >
                      <md-table :html="block.html">
                        <div v-html="block.html"></div>
                      </md-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 商品分析tab -->
          <template v-else-if="type === 'shop' && activeTab === 'PRODUCT'">
            <div
              v-for="(report, index) in productAnalysisList"
              :key="'product-' + index"
              class="ai-report-sale-section"
            >
              <div class="ai-report-sale-title">{{ report.reportName }}</div>
              <div v-if="report.noDataText" class="ai-report-sale-desc">
                {{ report.noDataText }}
              </div>
              <div v-else class="ai-report-table-wrapper">
                <div class="ai-report-table-scroll">
                  <el-table
                    :data="getProductTableData(report)"
                    border
                    style="width: 100%"
                    size="small"
                    :span-method="
                      [
                        'TOP10_PRODUCT_ASSOCIATION',
                        'TOP10_REPURCHASE_ASSOCIATION',
                      ].includes(report.reportType)
                        ? ({ row, column, rowIndex, columnIndex }) =>
                            productSpanMethod({
                              row,
                              column,
                              rowIndex,
                              columnIndex,
                              tableData: getProductTableData(report),
                            })
                        : undefined
                    "
                    :max-height="maxHeight"
                  >
                    <template slot="empty">
                      <empty />
                    </template>
                    <template
                      v-for="column in productAnalysisColumns[
                        report.reportType
                      ]"
                    >
                      <el-table-column
                        v-if="column.children"
                        :key="column.label"
                        :label="
                          getAssociationColumnLabel(column, report.reportType)
                        "
                        align="center"
                      >
                        <el-table-column
                          v-for="child in column.children"
                          :key="child.prop"
                          :prop="child.prop"
                          :label="child.label"
                          :min-width="child.minWidth"
                          align="center"
                          :formatter="
                            child.formatter ? child.formatter : undefined
                          "
                        />
                      </el-table-column>
                      <el-table-column
                        v-else
                        :key="column.prop"
                        :prop="column.prop"
                        :label="column.label"
                        :min-width="column.minWidth"
                        align="center"
                        :formatter="
                          column.formatter ? column.formatter : undefined
                        "
                      />
                    </template>
                  </el-table>
                </div>
              </div>
              <div
                v-for="(analysis, aIndex) in report.analyses"
                :key="'analysis-' + index + '-' + aIndex"
                class="ai-report-paragraph-section"
              >
                <div class="ai-report-subtitle">{{ analysis.title }}</div>
                <div
                  class="ai-report-text global-ai-content"
                >
                  <div
                    v-for="(block, idx) in renderedBlocks(analysis.content)"
                    :key="idx"
                  >
                    <!-- 普通 markdown 文本 -->
                    <div
                      v-if="block.type === 'markdown'"
                      class="md-content"
                      v-html="block.html"
                    ></div>
                    <!-- 表格块：按钮 + 表格 -->
                    <div
                      v-else-if="block.type === 'table'"
                      class="table-block"
                    >
                      <md-table :html="block.html">
                        <div v-html="block.html"></div>
                      </md-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <!-- 短信营销分析 -->
          <template
            v-else-if="type === 'message' && selectedValue === 'SUMMARY'"
          >
            <ai-report-sms-summary :summary="messageSummary" />
          </template>
          <!-- 其他tab/类型 -->
          <template v-else>
            <!-- 大标题 -->
            <div v-if="getNoDataText" class="ai-report-sale-desc">
              {{ getNoDataText }}
            </div>

            <!-- 表格区域 -->
            <div v-else class="ai-report-table-wrapper">
              <div class="ai-report-table-scroll">
                <!-- BI报表 -->
                <AiReportBI
                  v-if="type === 'sence'"
                  :current-path="selectedValue"
                  :max-height="biMaxHeight"
                  :reportList="reportList"
                />
                <el-table
                  v-else
                  :data="tableData"
                  border
                  style="width: 100%"
                  size="small"
                  :max-height="maxHeight"
                >
                  <template slot="empty">
                    <empty />
                  </template>
                  <el-table-column
                    v-for="column in tableColumns"
                    :key="column.prop"
                    :prop="column.prop"
                    :label="column.label"
                    :width="column.width"
                    :min-width="column.minWidth"
                    align="center"
                    :formatter="column.formatter"
                  />
                </el-table>
              </div>
            </div>

            <!-- 段落内容区域 -->
            <div class="ai-report-paragraphs">
              <div
                v-for="(analysis, index) in paragraphSections"
                :key="'section-' + index"
                class="ai-report-paragraph-section"
              >
                <div class="ai-report-subtitle">{{ analysis.title }}</div>
                <div
                  class="ai-report-text global-ai-content"
                >
                  <div
                    v-for="(block, idx) in renderedBlocks(analysis.content)"
                    :key="idx"
                  >
                    <!-- 普通 markdown 文本 -->
                    <div
                      v-if="block.type === 'markdown'"
                      class="md-content"
                      v-html="block.html"
                    ></div>
                    <!-- 表格块：按钮 + 表格 -->
                    <div
                      v-else-if="block.type === 'table'"
                      class="table-block"
                    >
                      <md-table :html="block.html">
                        <div v-html="block.html"></div>
                      </md-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </el-dialog>
    <!-- 添加备注 -->
    <addremarks
      v-if="isShowRecordExport"
      ref="addremarks"
      :biFlag="false"
      :control="isShowRecordExport"
      :jumpType="jumpType"
      @transData="(val) => (isShowRecordExport = val)"
      @transData1="exportTable"
    ></addremarks>
  </div>
</template>

<script>
import { marked } from "marked";
import AiReportBI from "./aiReportBI.vue";
import addremarks from "@/components/generate_report.vue";
import mdTable from "@/components/ai/components/mdTable.vue";
import Empty from "@/components/ai/components/prefabricateReport/components/empty.vue";
import AiReportSmsSummary from "./aiReportSmsSummary.vue";
import { getReportById, exportAiAnalysis } from "@/api/ai/newAi.js";
export default {
  name: "AiReportDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    platformCode: {
      type: String,
      default: "TB",
      validator: (v) => ["TB", "JD", "DY"].includes(v),
    },
    cardInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    AiReportBI,
    addremarks,
    mdTable,
    Empty,
    AiReportSmsSummary,
  },
  data() {
    return {
      cdnUrl: "https://cdn-image.yucrm.cn/images/tb-front-platform/images/ai/",
      dialogVisible: false,
      selectedValue: "",
      activeTab: "SALES",
      maxHeight: "308px",
      biMaxHeight: "348px",
      isShowRecordExport: false,
      // 报表枚举配置
      reportList: [],
      reportEnumList: [
        // 销售分析
        { label: "销售概览", value: "STORE_SALES" },
        { label: "地域销售总览", value: "AREA_SALES" },
        { label: "线级城市分析", value: "CLASS_CITY_SALES" },
        { label: "商品sku销售", value: "TOP10_PRODUCT_SALES_SKU" },
        { label: "商品spu销售", value: "TOP10_PRODUCT_SALES_SPU" },
        { label: "平台类目销售", value: "TOP10_PRODUCT_SALES_CATEGORY" },
        { label: "店铺类目销售", value: "TOP10_PRODUCT_SALES_STORE_CATEGORY" },
        { label: "自定义类目销售", value: "TOP10_PRODUCT_SALES_CUSTOM_CATEGORY" },
        { label: "商品标签销售", value: "TOP10_PRODUCT_SALES_TAG" },
        { label: "下单来源", value: "THEME_TYPE_SALES" },
        { label: "流量来源", value: "CBIZ_SALES" },
        { label: "主播交易", value: "AUTHOR_SALES" },
        { label: "直播间交易", value: "ROOM_SALES" },
        { label: "短视频交易", value: "VIDEO_SALES" },
        // 客户分析
        { label: "留存率分析", value: "RETENTION" },
        // { label: "留存分析-商品sku", value: "TOP10_RETENTION_ANALYSIS_SKU" },
        // { label: "留存分析-商品spu", value: "TOP10_RETENTION_ANALYSIS_SPU" },
        // { label: "留存分析-平台类目", value: "TOP10_RETENTION_ANALYSIS_CATEGORY" },
        // { label: "留存分析-店铺类目", value: "TOP10_RETENTION_ANALYSIS_STORE_CATEGORY" },
        // { label: "留存分析-商品标签", value: "TOP10_RETENTION_ANALYSIS_TAG" },
        { label: "客户生命周期", value: "CUSTOMER_LIFE_CYCLE" },
        { label: "购买金额分析", value: "PURCHASE_AMOUNT" },
        { label: "购买频次分析", value: "PURCHASE_FREQUENCY" },
        { label: "客单价分析", value: "AVG_ORDER_VALUE" },
        { label: "件单价分析", value: "ITEM_PRICE_ANALYSIS" },
        { label: "复购周期分析", value: "REPURCHASE_CYCLE" },
        { label: "客户复购-时间", value: "CUSTOMER_REPURCHASE_TIME" },
        { label: "客户复购-商品标签", value: "CUSTOMER_REPURCHASE" },
        // 会员分析
        { label: "会员概览", value: "MEMBER_OVERVIEW" },
        { label: "会员招募（京东）", value: "MEMBER_RECRUIT_JD" },
        { label: "会员招募（淘宝）", value: "MEMBER_RECRUIT_TB" },
        { label: "会员等级分析", value: "MEMBER_LEVEL_ANALYSIS" },
        { label: "会员等级分析（会员通）", value: "MEMBER_LEVEL_HYT_ANALYSIS" },
        { label: "会员地域销售", value: "MEMBER_GRADE_AREA_ANALYSIS" },
        { label: "会员积分分析", value: "MEMBER_POINTS_ANALYSIS" },
        { label: "积分变更分析", value: "POINTS_CHANGE_ANALYSIS" },
        { label: "会员生命周期", value: "MEMBER_LIFE_CYCLE" },
        { label: "会员等级购买金额", value: "MEMBER_GRADE_PURCHASE_AMOUNT" },
        { label: "会员等级购买频次", value: "MEMBER_GRADE_PURCHASE_FREQUENCY" },
        { label: "会员等级客单价", value: "MEMBER_GRADE_AVG_ORDER_VALUE" },
        { label: "会员等级件单价", value: "MEMBER_ITEM_PRICE_ANALYSIS" },
        { label: "会员等级复购周期", value: "MEMBER_GRADE_REPURCHASE_CYCLE" },
        // 关联分析
        { label: "同笔关联-商品sku", value: "TOP10_PRODUCT_ASSOCIATION_SKU" },
        { label: "同笔关联-商品spu", value: "TOP10_PRODUCT_ASSOCIATION_SPU" },
        {
          label: "同笔关联-平台类目",
          value: "TOP10_PRODUCT_ASSOCIATION_CATEGORY",
        },
        {
          label: "同笔关联-店铺类目",
          value: "TOP10_PRODUCT_ASSOCIATION_STORE_CATEGORY",
        },
        { label: "同笔关联-商品标签", value: "TOP10_PRODUCT_ASSOCIATION_TAG" },
        {
          label: "复购关联-商品sku",
          value: "TOP10_REPURCHASE_ASSOCIATION_SKU",
        },
        {
          label: "复购关联-商品spu",
          value: "TOP10_REPURCHASE_ASSOCIATION_SPU",
        },
        {
          label: "复购关联-平台类目",
          value: "TOP10_REPURCHASE_ASSOCIATION_CATEGORY",
        },
        {
          label: "复购关联-店铺类目",
          value: "TOP10_REPURCHASE_ASSOCIATION_STORE_CATEGORY",
        },
        {
          label: "复购关联-商品标签",
          value: "TOP10_REPURCHASE_ASSOCIATION_TAG",
        },
        { label: "交叉关联-商品sku", value: "TOP10_CROSS_ASSOCIATION_SKU" },
        { label: "交叉关联-商品spu", value: "TOP10_CROSS_ASSOCIATION_SPU" },
        { label: "交叉关联-平台类目", value: "TOP10_CROSS_ASSOCIATION_CATEGORY" },
        { label: "交叉关联-店铺类目", value: "TOP10_CROSS_ASSOCIATION_STORE_CATEGORY" },
        { label: "交叉关联-商品标签", value: "TOP10_CROSS_ASSOCIATION_TAG" },
        { label: "售前关联-商品sku", value: "TOP10_PRESALES_ASSOCIATION_SKU" },
        { label: "售前关联-商品spu", value: "TOP10_PRESALES_ASSOCIATION_SPU" },
        { label: "售前关联-平台类目", value: "TOP10_PRESALES_ASSOCIATION_CATEGORY" },
        { label: "售前关联-店铺类目", value: "TOP10_PRESALES_ASSOCIATION_STORE_CATEGORY" },
        { label: "售前关联-商品标签", value: "TOP10_PRESALES_ASSOCIATION_TAG" },
        // 复购分析
        {
          label: "同品复购-商品sku",
          value: "TOP10_SAME_PRODUCT_REPURCHASE_SKU",
        },
        {
          label: "同品复购-商品spu",
          value: "TOP10_SAME_PRODUCT_REPURCHASE_SPU",
        },
        {
          label: "同品复购-平台类目",
          value: "TOP10_SAME_PRODUCT_REPURCHASE_CATEGORY",
        },
        {
          label: "同品复购-店铺类目",
          value: "TOP10_SAME_PRODUCT_REPURCHASE_STORE_CATEGORY",
        },
        {
          label: "同品复购-商品标签",
          value: "TOP10_SAME_PRODUCT_REPURCHASE_TAG",
        },
        { label: "店铺复购-商品sku", value: "TOP10_STORE_REPURCHASE_SKU" },
        { label: "店铺复购-商品spu", value: "TOP10_STORE_REPURCHASE_SPU" },
        {
          label: "店铺复购-平台类目",
          value: "TOP10_STORE_REPURCHASE_CATEGORY",
        },
        {
          label: "店铺复购-店铺类目",
          value: "TOP10_STORE_REPURCHASE_STORE_CATEGORY",
        },
        { label: "店铺复购-商品标签", value: "TOP10_STORE_REPURCHASE_TAG" },
        // 购买分布
        // { label: "商品sku购买频次", value: "TOP10_ITEM_PURCHASE_FREQUENCY_SKU" },
        // { label: "商品spu购买频次", value: "TOP10_ITEM_PURCHASE_FREQUENCY_SPU" },
        // { label: "平台类目购买频次", value: "TOP10_ITEM_PURCHASE_FREQUENCY_CATEGORY" },
        // { label: "店铺类目购买频次", value: "TOP10_ITEM_PURCHASE_FREQUENCY_STORE_CATEGORY" },
        // { label: "商品标签购买频次", value: "TOP10_ITEM_PURCHASE_FREQUENCY_TAG" },
      ],
      tableData: [],
      messageSummary: {},
      analysisFields: [
        { key: "currentDescription", label: "现状描述" },
        { key: "dimensionComparison", label: "维度对比" },
        { key: "industryComparison", label: "行业对比" },
        { key: "problemDiscovery", label: "问题发现" },
        { key: "causeAnalysis", label: "原因分析" },
        {
          key: "operationalStrategySuggestions",
          label: "运营策略建议",
        },
      ],
      saleAnalysisColumns: [
        { prop: "dateRange", label: "时间", width: 220, align: "center" },
        { prop: "amountRatioStr", label: "金额占比", width: 120 },
        { prop: "buyerCount", label: "成交人数", width: 120, formatter: (row) => this.dealOtherValue(row, 'buyerCount') },
        { prop: "buyerRatioStr", label: "人数占比", width: 120 },
        { prop: "orderCount", label: "成交单量", width: 120, formatter: (row) => this.dealOtherValue(row, 'orderCount') },
        { prop: "itemCount", label: "成交件数", width: 120, formatter: (row) => this.dealOtherValue(row, 'itemCount') },
        { prop: "customerUnitPrice", label: "客单价", width: 120, formatter: (row) => this.dealOtherValue(row, 'customerUnitPrice') },
        { prop: "orderUnitPrice", label: "笔单价", width: 120, formatter: (row) => this.dealOtherValue(row, 'orderUnitPrice') },
        { prop: "itemUnitPrice", label: "件单价", width: 120, formatter: (row) => this.dealOtherValue(row, 'itemUnitPrice') },
        { prop: "avgPurchaseFrequency", label: "人均购买频次", width: 140, formatter: (row) => this.dealOtherValue(row, 'avgPurchaseFrequency') },
        { prop: "avgPurchaseItems", label: "人均购买件数", width: 140, formatter: (row) => this.dealOtherValue(row, 'avgPurchaseItems') },
        { prop: "avgItemsPerOrder", label: "笔均购买件数", width: 140, formatter: (row) => this.dealOtherValue(row, 'avgItemsPerOrder') },
      ],
      customerAnalysisCommonColumns: {
        PURCHASE_AMOUNT: [
          {
            prop: "cusTypeName",
            label: "客户类型",
            width: 100,
            align: "center",
          },
          {
            prop: "amountRange",
            label: "金额区间",
            width: 120,
            align: "center",
          },
          { prop: "paymentCount", label: "付款人数", width: 110 },
          { prop: "paymentCountRatioStr", label: "付款人数占比", width: 140 },
          { prop: "paymentAmount", label: "付款金额", width: 110 },
          { prop: "paymentAmountRatioStr", label: "付款金额占比", width: 140 },
          { prop: "paymentOrderCount", label: "付款单量", width: 110 },
          {
            prop: "paymentOrderCountRatioStr",
            label: "付款单量占比",
            width: 140,
          },
          { prop: "paymentItemCount", label: "付款件数", width: 110 },
          {
            prop: "paymentItemCountRatioStr",
            label: "付款件数占比",
            width: 140,
          },
          { prop: "customerUnitPrice", label: "客单价", width: 110 },
          { prop: "itemUnitPrice", label: "件单价", width: 110 },
          { prop: "orderUnitPrice", label: "笔单价", width: 110 },
        ],
        PURCHASE_FREQUENCY: [
          {
            prop: "buyerTypeName",
            label: "客户类型",
            width: 100,
            align: "center",
          },
          {
            prop: "frequencyInterval",
            label: "购买频次",
            width: 120,
            align: "center",
          },
          { prop: "payCount", label: "付款人数", width: 110 },
          { prop: "payCountRatioStr", label: "付款人数占比", width: 140 },
          { prop: "tradeAmount", label: "付款金额", width: 110 },
          { prop: "tradeAmountRatioStr", label: "付款金额占比", width: 140 },
          { prop: "payTimes", label: "付款单量", width: 110 },
          { prop: "payTimesRatioStr", label: "付款单量占比", width: 140 },
          { prop: "payNum", label: "付款件数", width: 110 },
          { prop: "payNumRatioStr", label: "付款件数占比", width: 140 },
          { prop: "payAvgAmount", label: "客单价", width: 110 },
          { prop: "numAvgAmount", label: "件单价", width: 110 },
          { prop: "timesAvgAmount", label: "笔单价", width: 110 },
        ],
        AVG_ORDER_VALUE: [
          {
            prop: "buyerTypeName",
            label: "客户类型",
            width: 100,
            align: "center",
          },
          {
            prop: "avgAmountInterval",
            label: "客单价区间",
            width: 120,
            align: "center",
          },
          { prop: "payCount", label: "付款人数", width: 110 },
          { prop: "payCountRatioStr", label: "付款人数占比", width: 140 },
          { prop: "tradeAmount", label: "付款金额", width: 110 },
          { prop: "tradeAmountRatioStr", label: "付款金额占比", width: 140 },
          { prop: "payTimes", label: "付款单量", width: 110 },
          { prop: "payTimesRatioStr", label: "付款单量占比", width: 140 },
          { prop: "payNum", label: "付款件数", width: 110 },
          { prop: "payNumRatioStr", label: "付款件数占比", width: 140 },
          { prop: "payAvgAmount", label: "客单价", width: 110 },
          { prop: "numAvgAmount", label: "件单价", width: 110 },
          { prop: "timesAvgAmount", label: "笔单价", width: 110 },
        ],
        REPURCHASE_CYCLE: [
          {
            prop: "cusTypeName",
            label: "客户类型",
            width: 100,
            align: "center",
          },
          {
            prop: "cycleRange",
            label: "复购周期区间",
            width: 130,
            align: "center",
          },
          { prop: "paymentCount", label: "付款人数", width: 110 },
          { prop: "paymentCountRatioStr", label: "付款人数占比", width: 140 },
          { prop: "paymentAmount", label: "付款金额", width: 110 },
          { prop: "paymentAmountRatioStr", label: "付款金额占比", width: 140 },
          { prop: "paymentOrderCount", label: "付款单量", width: 110 },
          {
            prop: "paymentOrderCountRatioStr",
            label: "付款单量占比",
            width: 140,
          },
          { prop: "paymentItemCount", label: "付款件数", width: 110 },
          {
            prop: "paymentItemCountRatioStr",
            label: "付款件数占比",
            width: 140,
          },
          { prop: "customerUnitPrice", label: "客单价", width: 110 },
          { prop: "itemUnitPrice", label: "件单价", width: 110 },
          { prop: "orderUnitPrice", label: "笔单价", width: 110 },
        ],
      },
      memberAnalysisColumns: {
        MEMBER_OVERVIEW: [
          { prop: "memberCount", label: "会员数", minWidth: 120 },
          { prop: "newMemberCount", label: "新增会员数", minWidth: 120 },
          { prop: "upgradeCount", label: "升级会员数", minWidth: 120 },
          { prop: "downgradeCount", label: "降级会员数", minWidth: 120 },
          { prop: "unbindCount", label: "解绑会员数", minWidth: 120 },
        ],
        POINTS_MEMBER_OVERVIEW: [
          { prop: "totalScore", label: "总积分", minWidth: 120 },
          { prop: "occurScore", label: "发放积分", minWidth: 120 },
          { prop: "consumedScore", label: "消耗积分", minWidth: 120 },
          { prop: "tradeScore", label: "交易积分", minWidth: 120 },
          { prop: "expiredScore", label: "过期积分", minWidth: 120 },
          { prop: "usageRateStr", label: "积分核销率", minWidth: 120 },
        ],
        POINTS_INTERACTION: [
          {
            prop: "changedMemberCount",
            label: "积分变更会员数",
            minWidth: 150,
          },
          { prop: "issuedMemberCount", label: "积分发放会员数", minWidth: 150 },
          {
            prop: "consumedMemberCount",
            label: "积分消耗会员数",
            minWidth: 150,
          },
          {
            prop: "tradeIssuedMemberCount",
            label: "交易发放会员数",
            minWidth: 120,
          },
          {
            prop: "expiredMemberCount",
            label: "积分过期会员数",
            minWidth: 150,
          },
        ],
        MEMBER_RECRUIT_JD: [
          { prop: "channelId", label: "渠道ID", minWidth: 100 },
          { prop: "channelName", label: "渠道名称", minWidth: 120 },
          { prop: "joinCount", label: "入会人数", minWidth: 110 },
          { prop: "recruitRatioStr", label: "招募占比", minWidth: 110 },
          { prop: "tradeCount", label: "成交人数", minWidth: 110 },
          { prop: "conversionRatioStr", label: "转化率", minWidth: 100 },
          { prop: "tradeAmount", label: "成交金额", minWidth: 110 },
          { prop: "tradeTimes", label: "成交单量", minWidth: 110 },
          { prop: "tradeNum", label: "成交件数", minWidth: 110 },
          { prop: "tradeAvgAmount", label: "客单价", minWidth: 110 },
        ],
        MEMBER_RECRUIT_TB: [
          { prop: "channelName", label: "渠道名称", minWidth: 120 },
          { prop: "joinCount", label: "入会人数", minWidth: 110 },
          { prop: "recruitRatioStr", label: "招募占比", minWidth: 110 },
          { prop: "tradeCount", label: "成交人数", minWidth: 110 },
          { prop: "conversionRatioStr", label: "转化率", minWidth: 100 },
          { prop: "tradeAmount", label: "成交金额", minWidth: 110 },
          { prop: "tradeTimes", label: "成交单量", minWidth: 110 },
          { prop: "tradeNum", label: "成交件数", minWidth: 110 },
          { prop: "tradeAvgAmount", label: "客单价", minWidth: 110 },
        ],
        MEMBER_LEVEL_ANALYSIS: [
          {
            prop: "memberGradeName",
            label: "会员等级",
            minWidth: 120,
            align: "center",
          },
          {
            prop: "memberCount",
            label: "会员数",
            minWidth: 120,
            align: "right",
          },
          {
            prop: "memberRatioStr",
            label: "会员数占比",
            minWidth: 120,
            align: "right",
          },
          {
            prop: "tradeCount",
            label: "会员成交人数",
            minWidth: 140,
            align: "right",
          },
          {
            prop: "tradeCountRatioStr",
            label: "会员成交人数占比",
            minWidth: 180,
            align: "right",
          },
          {
            prop: "tradeTimes",
            label: "会员成交单量",
            minWidth: 140,
            align: "right",
          },
          {
            prop: "tradeTimesRatioStr",
            label: "会员成交单量占比",
            minWidth: 180,
            align: "right",
          },
          {
            prop: "tradeAmount",
            label: "会员成交金额",
            minWidth: 140,
            align: "right",
          },
          {
            prop: "tradeAmountRatioStr",
            label: "会员成交金额占比",
            minWidth: 180,
            align: "right",
          },
          {
            prop: "tradeAvgAmount",
            label: "客单价",
            minWidth: 120,
            align: "right",
          },
          {
            prop: "changeMemberCount",
            label: "变迁会员数",
            minWidth: 120,
            align: "right",
          },
        ],
        MEMBER_POINTS_ANALYSIS: [
          { prop: "scoreInterval", label: "积分值区间", minWidth: 110 },
          { prop: "memberCount", label: "会员数", minWidth: 100 },
          { prop: "memberRatioStr", label: "会员数占比", minWidth: 110 },
          { prop: "tradeCount", label: "会员成交人数", minWidth: 130 },
          { prop: "tradeTimes", label: "会员成交单量", minWidth: 130 },
          { prop: "tradeAmount", label: "会员成交金额", minWidth: 130 },
          { prop: "tradeAvgAmount", label: "客单价", minWidth: 100 },
        ],
        POINTS_CHANGE_ANALYSIS: [
          { prop: "changeTypeName", label: "变更类型", minWidth: 110 },
          { prop: "detailTypeName", label: "来源明细", minWidth: 120 },
          { prop: "score", label: "积分", minWidth: 110 },
          { prop: "memberCount", label: "人数", minWidth: 110 },
          { prop: "scoreRatioStr", label: "积分数占比", minWidth: 120 },
          { prop: "memberRatioStr", label: "人数占比", minWidth: 110 },
        ],
      },
      memberAnalysisColumnsType: [
        "MEMBER_OVERVIEW",
        "POINTS_MEMBER_OVERVIEW",
        "POINTS_INTERACTION",
        "MEMBER_RECRUIT_JD",
        "MEMBER_RECRUIT_TB",
        "MEMBER_LEVEL_ANALYSIS",
        "MEMBER_POINTS_ANALYSIS",
        "POINTS_CHANGE_ANALYSIS",
      ],
      productAnalysisColumnsMap: {
        TOP10_PRODUCT_SALES: [
          {
            prop: "skuId",
            label: "SKU id",
            minWidth: 120,
            isSkuColumn: true,
          },
          { prop: "title", label: "商品标题", minWidth: 150 },
          {
            prop: "payCount",
            label: "付款人数",
            minWidth: 100,
            formatter: (row) => {
              return row.totalPayReport.payCount;
            },
          },
          {
            prop: "tradeAmount",
            label: "付款金额",
            minWidth: 100,
            formatter: (row) => {
              return row.totalPayReport.tradeAmount;
            },
          },
          {
            prop: "tradeAmountRatioStr",
            label: "金额占比",
            minWidth: 100,
            formatter: (row) => {
              return row.totalPayReport.tradeAmountRatioStr;
            },
          },
          {
            prop: "payTimes",
            label: "付款单量",
            minWidth: 100,
            formatter: (row) => {
              return row.totalPayReport.payTimes;
            },
          },
          {
            prop: "payNum",
            label: "付款件数",
            minWidth: 100,
            formatter: (row) => {
              return row.totalPayReport.payNum;
            },
          },
          {
            prop: "payAvgAmount",
            label: "客单价",
            minWidth: 100,
            formatter: (row) => {
              return row.totalPayReport.payAvgAmount;
            },
          },
          {
            label: "新客",
            children: [
              {
                prop: "newBuyerReport_payCount",
                label: "付款人数",
                minWidth: 100,
                formatter: (row) => {
                  return row.newBuyerReport.payCount;
                },
              },
              {
                prop: "newBuyerReport_tradeAmount",
                label: "付款金额",
                minWidth: 100,
                formatter: (row) => {
                  return row.newBuyerReport.tradeAmount;
                },
              },
              {
                prop: "newBuyerReport_payTimes",
                label: "付款单量",
                minWidth: 100,
                formatter: (row) => {
                  return row.newBuyerReport.payTimes;
                },
              },
              {
                prop: "newBuyerReport_payNum",
                label: "付款件数",
                minWidth: 100,
                formatter: (row) => {
                  return row.newBuyerReport.payNum;
                },
              },
              {
                prop: "newBuyerReport_payAvgAmount",
                label: "客单价",
                minWidth: 100,
                formatter: (row) => {
                  return row.newBuyerReport.payAvgAmount;
                },
              },
              {
                prop: "newBuyerReport_payCountRatioStr",
                label: "新客占比",
                minWidth: 100,
                formatter: (row) => {
                  return row.newBuyerReport.payCountRatioStr;
                },
              },
            ],
          },
          {
            label: "老客",
            children: [
              {
                prop: "oldBuyerReport_payCount",
                label: "付款人数",
                minWidth: 100,
                formatter: (row) => {
                  return row.oldBuyerReport.payCount;
                },
              },
              {
                prop: "oldBuyerReport_tradeAmount",
                label: "付款金额",
                minWidth: 100,
                formatter: (row) => {
                  return row.oldBuyerReport.tradeAmount;
                },
              },
              {
                prop: "oldBuyerReport_payTimes",
                label: "付款单量",
                minWidth: 100,
                formatter: (row) => {
                  return row.oldBuyerReport.payTimes;
                },
              },
              {
                prop: "oldBuyerReport_payNum",
                label: "付款件数",
                minWidth: 100,
                formatter: (row) => {
                  return row.oldBuyerReport.payNum;
                },
              },
              {
                prop: "oldBuyerReport_payAvgAmount",
                label: "客单价",
                minWidth: 100,
                formatter: (row) => {
                  return row.oldBuyerReport.payAvgAmount;
                },
              },
              {
                prop: "oldBuyerReport_payCountRatioStr",
                label: "老客占比",
                minWidth: 100,
                formatter: (row) => {
                  return row.oldBuyerReport.payCountRatioStr;
                },
              },
            ],
          },
          {
            label: "会员",
            children: [
              {
                prop: "memberReport_payCount",
                label: "付款人数",
                minWidth: 100,
                formatter: (row) => {
                  return row.memberReport.payCount;
                },
              },
              {
                prop: "memberReport_tradeAmount",
                label: "付款金额",
                minWidth: 100,
                formatter: (row) => {
                  return row.memberReport.tradeAmount;
                },
              },
              {
                prop: "memberReport_payTimes",
                label: "付款单量",
                minWidth: 100,
                formatter: (row) => {
                  return row.memberReport.payTimes;
                },
              },
              {
                prop: "memberReport_payNum",
                label: "付款件数",
                minWidth: 100,
                formatter: (row) => {
                  return row.memberReport.payNum;
                },
              },
              {
                prop: "memberReport_payAvgAmount",
                label: "客单价",
                minWidth: 100,
                formatter: (row) => {
                  return row.memberReport.payAvgAmount;
                },
              },
              {
                prop: "memberReport_payCountRatio",
                label: "会员占比",
                minWidth: 100,
                formatter: (row) => {
                  return row.memberReport.payCountRatioStr;
                },
              },
            ],
          },
        ],
        TOP10_PRODUCT_ASSOCIATION: [
          { prop: "skuId", label: "skuld", minWidth: 120 },
          { prop: "skuName", label: "商品名称", minWidth: 150 },
          { prop: "buyCount", label: "购买人数", minWidth: 100 },
          { prop: "buyOrderCount", label: "购买单量", minWidth: 100 },
          { prop: "buyItemCount", label: "购买件数", minWidth: 100 },
          { prop: "buyAmount", label: "购买金额", minWidth: 100 },
          { prop: "rank", label: "关联排名", minWidth: 100 },
          { prop: "relateSkuId", label: "关联skuld", minWidth: 120 },
          { prop: "relateSkuName", label: "关联商品名", minWidth: 150 },
          { prop: "relateCount", label: "关联人数", minWidth: 100 },
          { prop: "relateOrderCount", label: "关联订单数", minWidth: 150 },
          { prop: "relateItemCount", label: "关联件数", minWidth: 100 },
          { prop: "relateAmount", label: "关联金额", minWidth: 100 },
          { prop: "relateRateStr", label: "关联率", minWidth: 100 },
        ],
        TOP10_REPURCHASE_ASSOCIATION: [
          {
            label: "时间",
            children: [
              { prop: "skuId", label: "skuld", minWidth: 120 },
              { prop: "skuName", label: "商品名称", minWidth: 150 },
              { prop: "buyCount", label: "购买人数", minWidth: 100 },
              { prop: "buyOrderCount", label: "购买单量", minWidth: 100 },
              { prop: "buyItemCount", label: "购买件数", minWidth: 100 },
              { prop: "buyAmount", label: "购买金额", minWidth: 100 },
              { prop: "rank", label: "复购排名", minWidth: 100 },
              { prop: "repurchaseSkuId", label: "复购skuld", minWidth: 120 },
              { prop: "repurchaseSkuName", label: "复购商品名", minWidth: 150 },
              { prop: "repurchaseCount", label: "复购人数", minWidth: 100 },
              {
                prop: "repurchaseOrderCount",
                label: "复购单量",
                minWidth: 100,
              },
              { prop: "repurchaseItemCount", label: "复购件数", minWidth: 100 },
              { prop: "repurchaseAmount", label: "复购金额", minWidth: 100 },
              { prop: "repurchaseRateStr", label: "复购率", minWidth: 100 },
            ],
          },
        ],
        TOP10_SAME_PRODUCT_REPURCHASE: [
          {
            label: "时间",
            children: [
              { prop: "skuId", label: "skuld", minWidth: 120 },
              { prop: "skuName", label: "商品名称", minWidth: 150 },
              { prop: "paymentCount", label: "付款人数", minWidth: 100 },
              { prop: "paymentAmount", label: "付款金额", minWidth: 100 },
              { prop: "purchaseTimes", label: "购买次数", minWidth: 100 },
              { prop: "paymentItemCount", label: "付款件数", minWidth: 100 },
              {
                prop: "sameSkuRepurchaseCount",
                label: "同品复购人数",
                minWidth: 150,
              },
              {
                prop: "sameSkuRepurchaseRatioStr",
                label: "同品复购率",
                minWidth: 150,
              },
              {
                prop: "sameSkuRepurchaseAmount",
                label: "同品复购金额",
                minWidth: 150,
              },
              {
                prop: "sameSkuRepurchaseItemCount",
                label: "同品复购件数",
                minWidth: 150,
              },
              {
                prop: "averageIntervalDays",
                label: "平均间隔天数",
                minWidth: 150,
              },
              {
                prop: "sameSkuRepurchaseTimes",
                label: "同品复购次数",
                minWidth: 150,
              },
            ],
          },
        ],
        TOP10_STORE_REPURCHASE: [
          {
            label: "时间",
            children: [
              { prop: "skuId", label: "skuld", minWidth: 120 },
              { prop: "skuName", label: "商品名称", minWidth: 150 },
              { prop: "paymentCount", label: "付款人数", minWidth: 100 },
              { prop: "paymentAmount", label: "付款金额", minWidth: 100 },
              { prop: "purchaseTimes", label: "购买次数", minWidth: 100 },
              { prop: "paymentItemCount", label: "付款件数", minWidth: 100 },
              {
                prop: "storeRepurchaseCount",
                label: "店铺复购人数",
                minWidth: 150,
              },
              {
                prop: "storeRepurchaseRatioStr",
                label: "店铺复购率",
                minWidth: 150,
              },
              {
                prop: "storeRepurchaseAmount",
                label: "店铺复购金额",
                minWidth: 150,
              },
              {
                prop: "storeRepurchaseItemCount",
                label: "店铺复购件数",
                minWidth: 150,
              },
              {
                prop: "averageIntervalDays",
                label: "平均间隔天数",
                minWidth: 150,
              },
              {
                prop: "storeRepurchaseTimes",
                label: "店铺复购次数",
                minWidth: 150,
              },
            ],
          },
        ],
      },
      paragraphSections: [],
      exportType: 'xlsx',
    };
  },
  computed: {
    renderedBlocks() {
      return (text) => {
        return this.$parseMarkdownToBlocks(text);
      };
    },
    type() {
      if (this.cardInfo.scenarioSubType == 1) {
        return "shop";
      } else if (this.cardInfo.scenarioSubType == 2) {
        return "sence";
      } else if (this.cardInfo.scenarioSubType == 3) {
        return "message";
      } else if (this.cardInfo.scenarioSubType == 4) {
        return "hd";
      }
    },
    jumpType() {
      if (["shop", "sence"].includes(this.type)) {
        return 3;
      } else {
        return 1;
      }
    },
    selectorOptions() {
      if (
        this.type === "sence" ||
        this.type === "message" ||
        this.type === "hd"
      ) {
        return this.reportList.map((item) => {
          return {
            label: item.reportName,
            value:
              this.type == "sence" ||
              (this.type == "message" && item.reportType == "SUMMARY")
                ? item.reportType
                : item.recordId,
          };
        });
      }
      return [];
    },
    typeConfig() {
      const configs = {
        hd: {
          title: "互动活动分析",
          selectorLabel: "切换活动",
        },
        sence: {
          title: "场景报表分析",
          selectorLabel: "切换报表",
        },
        shop: {
          title: "复盘报告",
          selectorLabel: "切换活动",
        },
        message: {
          title: "短信营销分析",
          selectorLabel: "切换批次",
        },
      };
      return configs[this.type] || configs.shop;
    },
    saleAnalysisList() {
      if (!this.reportList || !this.reportList.length) return [];
      return this.reportList
        .filter((item) => item.biCategory === "SALES")
        .map((item) => {
          let tableData = [];
          try {
            tableData =
              typeof item.reportData === "string"
                ? JSON.parse(item.reportData)
                : item.reportData || [];
          } catch (e) {
            tableData = [];
          }
          const analyses = this.analysisFields
            .filter((f) => item[f.key] && item[f.key].content)
            .map((f) => ({
              title: item[f.key].title || f.label,
              content: item[f.key].content,
            }));
          return {
            reportName: item.reportName,
            reportType: item.reportType,
            noDataText: item.noDataText || "",
            tableData,
            analyses,
          };
        });
    },
    customerAnalysisList() {
      if (!this.reportList || !this.reportList.length) return [];
      return this.reportList
        .filter((item) => item.biCategory === "CUSTOMER")
        .map((item) => {
          let tableData = [];
          try {
            tableData =
              typeof item.reportData === "string"
                ? JSON.parse(item.reportData)
                : item.reportData || [];
          } catch (e) {
            tableData = [];
          }
          const analyses = this.analysisFields
            .filter((f) => item[f.key] && item[f.key].content)
            .map((f) => ({
              title: item[f.key].title || f.label,
              content: item[f.key].content,
            }));
          return {
            reportName: item.reportName,
            reportType: item.reportType,
            noDataText: item.noDataText || "",
            tableData,
            analyses,
          };
        });
    },
    memberAnalysisList() {
      if (!this.reportList || !this.reportList.length) return [];
      return this.reportList
        .filter((item) => {
          if (
            item.biCategory !== "MEMBER" ||
            !this.memberAnalysisColumnsType.includes(item.reportType)
          )
            return false;
          if (
            item.reportType == "MEMBER_RECRUIT_JD" &&
            this.platformCode != "JD"
          )
            return false;
          if (
            item.reportType == "MEMBER_RECRUIT_TB" &&
            this.platformCode != "TB"
          )
            return false;
          return true;
        })
        .map((item) => {
          let tableData = [];
          try {
            const reportData =
              typeof item.reportData === "string"
                ? JSON.parse(item.reportData)
                : item.reportData || [];
            if (item.reportType == "MEMBER_OVERVIEW") {
              tableData = [reportData.member];
            } else if (item.reportType == "POINTS_MEMBER_OVERVIEW") {
              tableData = [reportData.score];
            } else if (item.reportType == "POINTS_INTERACTION") {
              tableData = [reportData.interaction];
            } else {
              tableData = reportData;
            }
          } catch (e) {
            tableData = [];
          }
          const analyses = this.analysisFields
            .filter((f) => item[f.key] && item[f.key].content)
            .map((f) => ({
              title: item[f.key].title || f.label,
              content: item[f.key].content,
            }));
          return {
            reportName: item.reportName,
            reportType: item.reportType,
            noDataText: item.noDataText || "",
            tableData,
            analyses,
          };
        });
    },
    productAnalysisList() {
      if (!this.reportList || !this.reportList.length) return [];
      return this.reportList
        .filter((item) => item.biCategory === "PRODUCT")
        .map((item) => {
          let tableData = [];
          try {
            tableData =
              typeof item.reportData === "string"
                ? JSON.parse(item.reportData)
                : item.reportData || [];
          } catch (e) {
            tableData = [];
          }
          const analyses = this.analysisFields
            .filter((f) => item[f.key] && item[f.key].content)
            .map((f) => ({
              title: item[f.key].title || f.label,
              content: item[f.key].content,
            }));
          return {
            reportName: item.reportName,
            reportType: item.reportType,
            noDataText: item.noDataText || "",
            tableData,
            analyses,
          };
        });
    },
    productAnalysisColumns() {
      const map = this.productAnalysisColumnsMap;
      const skuLabel = this.platformCode === "JD" ? "SKU id" : "商品id";
      const skuProp = this.platformCode === "JD" ? "skuId" : "numiid";
      const result = {};
      Object.keys(map).forEach((name) => {
        result[name] = map[name].map((col) => {
          if (col.isSkuColumn) {
            return { ...col, prop: skuProp, label: skuLabel };
          }
          if (col.children) {
            return {
              ...col,
              children: col.children.map((c) => ({ ...c })),
            };
          }
          return { ...col };
        });
      });
      return result;
    },
    tableColumns() {
      // hd类型的表格列定义
      const hdColumns = {
        // 京东 JD - hd类型
        JD: [
          { prop: "activityName", label: "活动名称", width: 140 },
          {
            prop: "activityType",
            label: "活动类型",
            width: 120,
            formatter: (row) => {
              return this.$getActivityNameByValue(row.activityType);
            },
          },
          { prop: "activityTime", label: "活动时间", width: 160 },
          { prop: "activityPv", label: "活动PV", width: 120, align: "right" },
          { prop: "activityUv", label: "活动UV", width: 120, align: "right" },
          {
            prop: "participateCount",
            label: "参与次数",
            width: 120,
            align: "right",
          },
          {
            prop: "participateUserCount",
            label: "参与人数",
            width: 120,
            align: "right",
          },
          {
            prop: "activityParticipateRate",
            label: "活动参与率",
            width: 120,
            align: "right",
          },
          {
            prop: "newMemberParticipateCount",
            label: "新会员参与次数",
            width: 150,
            align: "right",
          },
          {
            prop: "newMemberParticipateUserCount",
            label: "新会员参与人数",
            width: 150,
            align: "right",
          },
          {
            prop: "newMemberParticipateRate",
            label: "新会员参与率",
            width: 140,
            align: "right",
          },
          {
            prop: "oldMemberParticipateCount",
            label: "老会员参与次数",
            width: 150,
            align: "right",
          },
          {
            prop: "oldMemberParticipateUserCount",
            label: "老会员参与人数",
            width: 150,
            align: "right",
          },
          {
            prop: "oldMemberParticipateRate",
            label: "老会员参与率",
            width: 140,
            align: "right",
          },
          {
            prop: "shareActivityCount",
            label: "分享活动次数",
            width: 140,
            align: "right",
          },
          {
            prop: "shareActivityUserCount",
            label: "分享活动人数",
            width: 140,
            align: "right",
          },
          {
            prop: "activityShareRate",
            label: "活动分享率",
            width: 120,
            align: "right",
          },
          {
            prop: "participateNotWinCount",
            label: "参与未中奖",
            width: 120,
            align: "right",
          },
          {
            prop: "participateWinCount",
            label: "参与已中奖",
            width: 120,
            align: "right",
          },
          { prop: "winCount", label: "中奖次数", width: 120, align: "right" },
          {
            prop: "winUserCount",
            label: "中奖人数/领奖人数",
            width: 170,
            align: "right",
          },
          {
            prop: "addressFilledUserCount",
            label: "已填写地址人数",
            width: 150,
            align: "right",
          },
          {
            prop: "addressNotFilledUserCount",
            label: "未填写地址人数",
            width: 150,
            align: "right",
          },
          { prop: "shopPv", label: "店铺PV", width: 120, align: "right" },
          { prop: "shopUv", label: "店铺UV", width: 120, align: "right" },
          {
            prop: "shopViewRate",
            label: "店铺浏览率",
            width: 130,
            align: "right",
          },
          {
            prop: "followShopCount",
            label: "关注店铺次数",
            width: 140,
            align: "right",
          },
          {
            prop: "followShopUserCount",
            label: "关注店铺人数",
            width: 140,
            align: "right",
          },
          {
            prop: "shopFollowRate",
            label: "店铺关注率",
            width: 130,
            align: "right",
          },
          { prop: "productPv", label: "商品PV", width: 120, align: "right" },
          { prop: "productUv", label: "商品UV", width: 120, align: "right" },
          {
            prop: "productViewRate",
            label: "商品浏览率",
            width: 130,
            align: "right",
          },
          {
            prop: "orderUserCount",
            label: "下单人数",
            width: 120,
            align: "right",
          },
          {
            prop: "orderCount",
            label: "下单订单数",
            width: 130,
            align: "right",
          },
          {
            prop: "orderAmount",
            label: "下单金额",
            width: 120,
            align: "right",
          },
          {
            prop: "orderProductCount",
            label: "下单件数",
            width: 120,
            align: "right",
          },
          {
            prop: "orderCustomerPrice",
            label: "客单价（下单）",
            width: 140,
            align: "right",
          },
          {
            prop: "orderProductPrice",
            label: "件单价（下单）",
            width: 140,
            align: "right",
          },
          {
            prop: "orderConversionRate",
            label: "下单转化率",
            width: 130,
            align: "right",
          },
          {
            prop: "payUserCount",
            label: "付款人数",
            width: 120,
            align: "right",
          },
          {
            prop: "payOrderCount",
            label: "付款订单数",
            width: 130,
            align: "right",
          },
          { prop: "payAmount", label: "付款金额", width: 120, align: "right" },
          {
            prop: "payProductCount",
            label: "付款件数",
            width: 120,
            align: "right",
          },
          {
            prop: "payCustomerPrice",
            label: "客单价（付款）",
            width: 140,
            align: "right",
          },
          {
            prop: "payProductPrice",
            label: "件单价（付款）",
            width: 140,
            align: "right",
          },
          {
            prop: "orderPayConversionRate",
            label: "下单-付款转化率",
            width: 150,
            align: "right",
          },
          {
            prop: "payConversionRate",
            label: "付款转化率",
            width: 120,
            align: "right",
          },
          {
            prop: "completeUserCount",
            label: "完成人数",
            width: 120,
            align: "right",
          },
          {
            prop: "completeOrderCount",
            label: "完成订单数",
            width: 130,
            align: "right",
          },
          {
            prop: "completeAmount",
            label: "完成金额",
            width: 120,
            align: "right",
          },
          {
            prop: "completeProductCount",
            label: "完成件数",
            width: 120,
            align: "right",
          },
          {
            prop: "completeCustomerPrice",
            label: "客单价（完成）",
            width: 140,
            align: "right",
          },
          {
            prop: "completeProductPrice",
            label: "件单价（完成）",
            width: 140,
            align: "right",
          },
          {
            prop: "orderCompleteConversionRate",
            label: "下单-完成转化率",
            width: 150,
            align: "right",
          },
          {
            prop: "completeConversionRate",
            label: "完成转化率",
            width: 120,
            align: "right",
          },
          {
            prop: "orderNotPayUserCount",
            label: "下单未付款人数",
            width: 150,
            align: "right",
          },
          {
            prop: "notPayOrderCount",
            label: "未付款订单数",
            width: 140,
            align: "right",
          },
          {
            prop: "notPayAmount",
            label: "未付款金额",
            width: 130,
            align: "right",
          },
          {
            prop: "notPayProductCount",
            label: "未付款件数",
            width: 130,
            align: "right",
          },
          {
            prop: "orderCancelRate",
            label: "订单取消率",
            width: 130,
            align: "right",
          },
          {
            prop: "refundUserCount",
            label: "退款人数",
            width: 120,
            align: "right",
          },
          {
            prop: "refundOrderCount",
            label: "退款订单数",
            width: 130,
            align: "right",
          },
          {
            prop: "refundAmount",
            label: "退款金额",
            width: 120,
            align: "right",
          },
          {
            prop: "refundProductCount",
            label: "退款件数",
            width: 120,
            align: "right",
          },
          { prop: "refundRate", label: "退款率", width: 110, align: "right" },
          {
            prop: "newMemberCount",
            label: "新增入会人数",
            width: 140,
            align: "right",
          },
          { prop: "memberRate", label: "入会率", width: 110, align: "right" },
          {
            prop: "newMemberPayUserCount",
            label: "新增会员付款人数",
            width: 160,
            align: "right",
          },
          {
            prop: "newMemberPayOrderCount",
            label: "新增会员付款订单数",
            width: 170,
            align: "right",
          },
          {
            prop: "newMemberPayAmount",
            label: "新增会员付款金额",
            width: 160,
            align: "right",
          },
          {
            prop: "newMemberPayCustomerPrice",
            label: "新增会员客单价（付款）",
            width: 200,
            align: "right",
          },
          {
            prop: "newMemberCompleteUserCount",
            label: "新增会员完成人数",
            width: 160,
            align: "right",
          },
          {
            prop: "newMemberCompleteOrderCount",
            label: "新增会员完成订单数",
            width: 170,
            align: "right",
          },
          {
            prop: "newMemberCompleteAmount",
            label: "新增会员完成金额",
            width: 160,
            align: "right",
          },
          {
            prop: "newMemberCompleteCustomerPrice",
            label: "新增会员客单价（完成）",
            width: 200,
            align: "right",
          },
        ],
      };

      // shop类型的表格列定义
      const msgColumns = {
        // 京东 JD
        JD: [
          { prop: "activityTitle", label: "活动主题", width: 140 },
          { prop: "activeObject", label: "活动对象", width: 120 },
          {
            prop: "smsType",
            label: "短信类型",
            width: 120,
            formatter: (row) =>
              row.smsType === 0
                ? "普通短信"
                : row.smsType === 1
                ? "富信"
                : row.smsType === 2
                ? "卡片短信"
                : "",
          },
          { prop: "content", label: "短信内容", width: 200 },
          { prop: "sendTime", label: "发送时间", width: 160 },
          { prop: "effectTime", label: "效果数据时间", width: 160 },
          {
            prop: "roi",
            label: "ROI（投入产出比）",
            width: 160,
            align: "right",
          },
          {
            prop: "sendSubmitNum",
            label: "预计触达人数",
            width: 130,
            align: "right",
          },
          {
            prop: "sendNum",
            label: "实际触达人数",
            width: 130,
            align: "right",
          },
          {
            prop: "rate",
            label: "触达率（%）",
            width: 140,
            align: "right",
          },
          {
            prop: "pv",
            label: "短链点击PV",
            width: 120,
            align: "right",
          },
          {
            prop: "uv",
            label: "短链点击UV",
            width: 120,
            align: "right",
          },
          {
            prop: "skuPvNum",
            label: "商品详情页PV",
            width: 140,
            align: "right",
          },
          {
            prop: "skuUvNum",
            label: "商品详情页UV",
            width: 140,
            align: "right",
          },
          {
            prop: "shopPvNum",
            label: "店铺首页PV",
            width: 130,
            align: "right",
          },
          {
            prop: "shopUvNum",
            label: "店铺首页UV",
            width: 130,
            align: "right",
          },
          {
            prop: "orderRate",
            label: "下单转化率（%）",
            width: 150,
            align: "right",
          },
          { prop: "ordPins", label: "下单人数", width: 110, align: "right" },
          {
            prop: "ordAmount",
            label: "下单金额",
            width: 110,
            align: "right",
          },
          {
            prop: "ordNum",
            label: "下单订单数",
            width: 110,
            align: "right",
          },
          {
            prop: "ordQtty",
            label: "下单件数",
            width: 110,
            align: "right",
          },
          {
            prop: "orderPayRate",
            label: "下单-付款转化率（%）",
            width: 200,
            align: "right",
          },
          { prop: "payRate", label: "付款转化率", width: 120, align: "right" },
          { prop: "payPins", label: "付款人数", width: 110, align: "right" },
          { prop: "payAmount", label: "付款金额", width: 110, align: "right" },
          {
            prop: "payNum",
            label: "付款订单数",
            width: 120,
            align: "right",
          },
          {
            prop: "payQtty",
            label: "付款件数",
            width: 110,
            align: "right",
          },
          {
            prop: "perCustomerTransaction",
            label: "客单价",
            width: 110,
            align: "right",
          },
          {
            prop: "followSkuNum",
            label: "关注商品人数",
            width: 130,
            align: "right",
          },
          {
            prop: "followShopNum",
            label: "关注店铺人数",
            width: 130,
            align: "right",
          },
          { prop: "cartNum", label: "加购人数", width: 110, align: "right" },
          {
            prop: "memberCardUserCnt",
            label: "会员开卡数",
            width: 120,
            align: "right",
          },
          {
            prop: "getCouponsCnt",
            label: "领券次数",
            width: 120,
            align: "right",
          },
          {
            prop: "getCouponsUserCnt",
            label: "领券人数",
            width: 110,
            align: "right",
          },
          {
            prop: "memberBuyPins",
            label: "已购会员购买客户数",
            width: 190,
            align: "right",
          },
          {
            prop: "brandMemberBuyPins",
            label: "开卡会员购买客户数",
            width: 190,
            align: "right",
          },
          {
            prop: "brandMemberNewBuyPins",
            label: "开卡会员购买新客户数",
            width: 190,
            align: "right",
          },
        ],
      };

      if (this.type === "hd") {
        return hdColumns[this.platformCode];
      } else {
        const fields =
          this.tableData && this.tableData.length
            ? Object.keys(this.tableData[0])
            : [];
        const visibleColumns = msgColumns[this.platformCode]
          .filter((item) => fields.includes(item.prop))
          .map((item) => {
            return {
              prop: item.prop,
              label: item.label,
              align: item.align,
              minWidth: item.width,
              formatter: item.formatter,
            };
          });
        return visibleColumns;
      }
    },
    getProductTableData() {
      return (report) => {
        if (
          [
            "TOP10_PRODUCT_ASSOCIATION",
            "TOP10_REPURCHASE_ASSOCIATION",
          ].includes(report.reportType)
        ) {
          const tableList = [];
          report.tableData.forEach((item) => {
            if (report.reportType == "TOP10_PRODUCT_ASSOCIATION") {
              if (item.relateList && item.relateList.length > 0) {
                item.relateList.forEach((item2) => {
                  tableList.push({
                    ...item,
                    ...item2,
                  });
                });
              } else {
                tableList.push({
                  ...item,
                });
              }
            } else if (report.reportType == "TOP10_REPURCHASE_ASSOCIATION") {
              if (item.repurchaseList && item.repurchaseList.length > 0) {
                item.repurchaseList.forEach((item2) => {
                  tableList.push({
                    ...item,
                    ...item2,
                  });
                });
              } else {
                tableList.push({
                  ...item,
                });
              }
            }
          });
          return tableList;
        } else {
          return report.tableData;
        }
      };
    },
    getAssociationColumnLabel() {
      return (column, reportType) => {
        if (reportType == "TOP10_REPURCHASE_ASSOCIATION") {
          return this.generateHeadText();
        } else if (
          ["TOP10_SAME_PRODUCT_REPURCHASE", "TOP10_STORE_REPURCHASE"].includes(
            reportType
          )
        ) {
          return this.generateHeadTextSame(reportType);
        } else {
          return column.label;
        }
      };
    },
    getNoDataText() {
      const key = this.type == "sence" ? "reportType" : "recordId";
      const selectedReport = this.reportList.find(
        (report) => report[key] === this.selectedValue
      );
      if (selectedReport) {
        return selectedReport.noDataText;
      } else {
        return "";
      }
    },
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
      },
      immediate: true,
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit("update:visible", false);
      }
    },
    platformCode: {
      handler() {
        this.updateTableData();
      },
    },
    type: {
      handler() {
        this.updateTableData();
      },
    },
    activeTab() {
      this.$nextTick(() => {
        const main = this.$el.querySelector(".ai-report-main");
        if (main) main.scrollTop = 0;
      });
    },
  },
  created() {
    this.updateTableData();
  },
  methods: {
    compileMarkdown(text) {
      return marked(text); // 直接使用
    },
    generateHeadText() {
      const report = this.reportList.filter(
        (report) => report.reportType === "TOP10_REPURCHASE_ASSOCIATION"
      );
      if (report && report.length) {
        if (report[0].date) {
          const payTimeStart = report[0].date.split("至")[0];
          const payTimeEnd = report[0].date.split("至")[1];
          return `(付款时间${this.formatDateStr(
            payTimeStart
          )}00:00:00-${this.formatDateStr(
            payTimeEnd
          )}23:59:59，复购时间${this.formatDateStr(
            payTimeStart
          )}00:00:00-${this.formatDateStr(payTimeEnd)}23:59:59)`;
        }
      }
    },
    generateHeadTextSame(reportType) {
      const report = this.reportList.filter(
        (report) => report.reportType === reportType
      );
      if (report && report.length) {
        if (report[0].date) {
          const payTimeStart = report[0].date.split("至")[0];
          const payTimeEnd = report[0].date.split("至")[1];
          return `(付款时间${this.formatDateStr(
            payTimeStart
          )}00:00:00-${this.formatDateStr(payTimeEnd)}23:59:59)`;
        }
      }
    },
    formatDateStr(dateStr) {
      if (!dateStr) return "";
      const [y, m, d] = dateStr.split("-");
      return `${y}年${m}月${d}日`;
    },
    updateTableData() {
      getReportById(this.cardInfo.chatId).then((res) => {
        console.log(JSON.parse(res.data));
        this.reportList = JSON.parse(res.data);
        if (this.reportList && this.reportList.length) {
          if (
            this.type === "message" ||
            this.type === "hd" ||
            this.type === "sence"
          ) {
            const itemObj = this.reportList[0];
            if (this.type == 'message' && itemObj.reportType == 'SUMMARY') {
              this.messageSummary = itemObj.summary? itemObj.summary : {};
              this.selectedValue = itemObj.reportType;
            } else {
              this.tableData = itemObj.reportData
              ? [JSON.parse(itemObj.reportData)]
              : [];
              this.selectedValue =
                this.type === "sence" ? itemObj.reportType : itemObj.recordId;
              this.paragraphSections = this.analysisFields
                .filter((f) => itemObj[f.key] && itemObj[f.key].content)
                .map((f) => ({
                  title: itemObj[f.key].title || f.label,
                  content: itemObj[f.key].content,
                }));
            }
          }
        }
      });
    },
    selectChange(e) {
      if (
        this.type === "message" ||
        this.type === "hd" ||
        this.type === "sence"
      ) {
        const key =
          this.type == "sence" || (this.type == "message" && e == "SUMMARY")
            ? "reportType"
            : "recordId";
        const selectedReport = this.reportList.find(
          (report) => report[key] === e
        );
        if (selectedReport && (selectedReport.reportData || selectedReport.summary)) {
          if (this.type == "message" && e == "SUMMARY") {
            this.messageSummary = selectedReport.summary;
          } else {
            this.tableData = [JSON.parse(selectedReport.reportData)];
            this.paragraphSections = this.analysisFields
              .filter(
                (f) => selectedReport[f.key] && selectedReport[f.key].content
              )
              .map((f) => ({
                title: selectedReport[f.key].title || f.label,
                content: selectedReport[f.key].content,
              }));
          }
        }
      }
    },
    handleClose() {
      this.dialogVisible = false;
    },
    handleDownload(type = 'xlsx') {
      this.exportType = type;
      this.isShowRecordExport = true;
    },
    customerSpanMethod({
      row,
      column,
      rowIndex,
      columnIndex,
      tableData,
      reportType,
    }) {
      if (columnIndex === 0) {
        // 只对客户类型列进行合并
        let key = "";
        if (["PURCHASE_AMOUNT", "REPURCHASE_CYCLE"].includes(reportType)) {
          key = "cusTypeName";
        } else if (
          ["PURCHASE_FREQUENCY", "AVG_ORDER_VALUE"].includes(reportType)
        ) {
          key = "buyerTypeName";
        }
        const sameTypeRows = tableData.filter((item) => item[key] === row[key]);
        if (
          rowIndex === tableData.findIndex((item) => item[key] === row[key])
        ) {
          // 如果是同类型的第一行，返回合并的行数
          return {
            rowspan: sameTypeRows.length,
            colspan: 1,
          };
        } else {
          // 其他行不显示
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
      }
    },
    memberSpanMethod({ row, column, rowIndex, columnIndex, tableData }) {
      if (columnIndex === 0) {
        // 只对客户类型列进行合并
        let key = "changeTypeName";
        const sameTypeRows = tableData.filter((item) => item[key] === row[key]);
        if (
          rowIndex === tableData.findIndex((item) => item[key] === row[key])
        ) {
          // 如果是同类型的第一行，返回合并的行数
          return {
            rowspan: sameTypeRows.length,
            colspan: 1,
          };
        } else {
          // 其他行不显示
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
      }
    },
    productSpanMethod({ row, column, rowIndex, columnIndex, tableData }) {
      // 需要合并的字段列表
      const mergeFields = [
        "skuId",
        "skuName",
        "buyCount",
        "buyOrderCount",
        "buyItemCount",
        "buyAmount",
      ];
      // 获取当前列的prop
      const prop = column.property;
      // 如果不是需要合并的字段，不进行合并
      if (!mergeFields.includes(prop)) {
        return {
          rowspan: 1,
          colspan: 1,
        };
      }
      // 查找相同数据的行范围
      if (rowIndex > 0) {
        const prevRow = tableData[rowIndex - 1];
        const currentRow = tableData[rowIndex];
        // 检查当前行和前一行是否需要合并
        let shouldMerge = true;
        for (const field of mergeFields) {
          if (prevRow[field] !== currentRow[field]) {
            shouldMerge = false;
            break;
          }
        }
        if (shouldMerge) {
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
      }
      // 计算当前相同数据的行数
      let rowspan = 1;
      for (let i = rowIndex + 1; i < tableData.length; i++) {
        const nextRow = tableData[i];
        let isSame = true;

        for (const field of mergeFields) {
          if (tableData[rowIndex][field] !== nextRow[field]) {
            isSame = false;
            break;
          }
        }
        if (isSame) {
          rowspan++;
        } else {
          break;
        }
      }
      return {
        rowspan: rowspan,
        colspan: 1,
      };
    },
    exportTable(remark) {
      const params = {
        exportRemark: remark,
        msgId: this.cardInfo.chatId,
      }
      if (this.exportType === 'ppt' && this.type == "message") {
        params.exportType = "PPT";
      }
      exportAiAnalysis(params).then((res) => {
        if (res.code == 200) {
          this.$refs.addremarks.showSuccessDailog();
        }
      });
    },
    // 处理特殊字段
    dealOtherValue(row, prop) {
      if (["同比比率", "环比比率"].includes(row.dateRange)) {
        if (["buyerCount", "orderCount", "itemCount"].includes(prop)) {
          return row[prop + 'Str'] + '%';
        } else {
          return row[prop] + '%';
        }
      }
      return row[prop];
    }
  },
};
</script>

<style lang="scss" scoped>
.dialog-title {
  font-weight: 500;
  font-size: 16px;
  color: #333333;
}
.dialog-sub {
  font-size: 12px;
  color: #999999;
  margin-left: 16px;
}
.ai-report-dialog {
  ::v-deep .el-dialog {
    border-radius: 16px !important;
    max-height: 800px;
    display: flex;
    flex-direction: column;
    margin-top: 0px !important;
    top: 50%;
    transform: translate(0px, -50%);
  }
  ::v-deep .el-dialog__header {
    padding: 14px 24px 10px;
    margin-right: 0;
    border-bottom: 1px solid #f2f2f2;
    display: flex;
    align-items: center;
    border-top-right-radius: 16px;
    border-top-left-radius: 16px;
    height: auto;
    line-height: normal;
  }

  ::v-deep .el-dialog__title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
  }

  ::v-deep .el-dialog__body {
    padding: 0px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overflow: hidden;
    height: 745px;
  }
}

.ai-report-content {
  width: 100%;
  height: 100%;
}

.ai-report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 24px 24px 0px 24px;
  margin-bottom: 16px;
}

.ai-report-selector {
  display: flex;
  align-items: center;

  .selector-label {
    font-size: 14px;
    color: #666;
    margin-right: 8px;
  }
}

.ai-report-download-message {
  display: flex;
  align-items: center;
  gap: 30px;
}

.ai-report-download {
  display: flex;
  align-items: center;
  &__shop {
    position: absolute;
    right: 24px;
    top: 24px;
  }
  &__img {
    width: 16px;
    height: 16px;
    margin-right: 2px;
  }
  .el-button {
    color: #3159ff;
    font-size: 12px;
  }
}

.ai-report-tabs {
  flex: 1;
  ::v-deep .el-tabs__header {
    margin-bottom: 0px;
  }
}

.ai-report-main {
  padding-top: 10px;
  overflow-y: auto;
  height: calc(100% - 47px);
  padding: 0px 12px 24px 24px;
  flex: 1;
}

.ai-report-main-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.5;
}

.ai-report-table-wrapper {
  width: 100%;
  margin-bottom: 24px;
}

.ai-report-table-scroll {
  width: 100%;
  overflow-x: auto;

  ::v-deep .el-table {
    font-size: 14px;
    border-radius: 4px;
    overflow: hidden;

    th.el-table__cell {
      background: #eff2f5;
      color: #333333;
      font-weight: 500;
      font-size: 14px;
      border-right: 1px solid #e5e9f2;

      .cell {
        white-space: nowrap;
      }
    }

    td.el-table__cell,
    th.el-table__cell {
      padding: 10px 8px;
    }

    td.el-table__cell .cell {
      white-space: pre-line;
      line-height: 1.5;
    }
  }
}

.ai-report-paragraphs {
  width: 100%;
}

.ai-report-paragraph-section {
  margin-bottom: 20px;
}

.ai-report-subtitle {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.5;
}

.ai-report-sale-section {
  margin-bottom: 24px;
}

.ai-report-sale-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.5;
}

.ai-report-sale-desc {
  font-size: 13px;
  color: #999;
  margin-bottom: 12px;
  line-height: 1.5;
}
</style>
